import React, { useContext, useEffect, useMemo } from 'react';
import { TranslationKey } from './../../../../../../../../types/translation/TranslationKey';
import {
  ProductTransaction,
  ProductTransactionStatus,
  ProductTransactionType,
} from '../../../../../../../../types/productTransaction';
import { Grid } from '@mui/material';
import { ProductMasterData } from '../../../../../../../../types/product_master_data/productMasterData';
import { OrderParentType } from '../../../../../../../../types/order';
import { t } from '../../../../../../../../types/translation/Translator';
import { BinContext } from '../../../../../../../../context/BinContext';
import { OrderContext } from '../../../../../../../../context/OrderContext';
import { getQuantityByBinForProduct } from '../../../../../../../../util/product.util';
import { ProductContext } from '../../../../../../../../context/ProductContext';
import { BinStatusContext } from '../../../../../../../../context/BinStatusContext';
import { Container } from '../../../../../../../../types/container';
import { ContainerContext } from '../../../../../../../../context/ContainerContext';
import { ProductTransactionContext } from '../../../../../../../../context/ProductTransactionContext';
import { Bin, SystemBin } from '../../../../../../../../types/bin';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';
import { CompanyContext } from '../../../../../../../../context/CompanyContext';
import { BinConstraintContext } from '../../../../../../../../context/BinConstraintContext';
import { UnitOfMeasure } from '../../../../../../../../types/uom';
import { ProcessTransactionProps } from '../ProcessOrderTransactionDefaultBody';
import { SystemStockLocation } from '../../../../../../../../types/stock_location/stockLocation';

interface ProductTransactionDestinationBinProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  disabled?: boolean;

  props: ProcessTransactionProps;
}

export default function ProductTransactionDestinationBin({
  transaction,
  setTransaction,
  productMasterData,
  container,
  disabled = false,
  props,
}: ProductTransactionDestinationBinProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { bins } = useContext(BinContext);
  const { orders } = useContext(OrderContext);
  const { containers } = useContext(ContainerContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { products } = useContext(ProductContext);
  const { productTransactions } = useContext(ProductTransactionContext);
  const { validateBinConstraints } = useContext(BinConstraintContext);

  const containerTransaction = productTransactions.get(transaction.containerTransactionId || 'NONE');

  const allowBinMismatch = currentCompany.settings.featureToggles.containers.allowBinMismatch;

  const toContainer = containers.get(props.toContainerId || '');
  const isVirtualContainer = toContainer?.stockLocationId === SystemStockLocation.id;

  const isDestinationBinDisabled = () => {
    if (transaction.status !== ProductTransactionStatus.created) return true;

    if (disabled) return true;

    if (
      orders.get(transaction.parentId || '')?.parentType === OrderParentType.task &&
      transaction.type === ProductTransactionType.inbound
    )
      return true;

    if (!!containerTransaction?.product.toBinId) return true;

    if (!!toContainer && !isVirtualContainer && !allowBinMismatch) return true;

    if (!transaction.product.toStockLocationId) return true;

    return false;
  };

  const binsForProduct = useMemo(() => {
    if (!productMasterData) return new Map();
    return getQuantityByBinForProduct(
      [...(products.get(productMasterData.id)?.values() || [])],
      transaction.product.lotId,
    );
  }, [products, productMasterData]);

  const validBins = useMemo(() => {
    const inboundAllowedBinIds = new Set<string>(
      [...bins.values()]
        .filter(bin => binStatuses.get(bins.get(bin.id)?.binStatusId || '')?.inboundAllowed)
        .map(bin => bin.id),
    );

    const validBins = [...bins.values()].filter(b => {
      if (b.companyId !== transaction.companyId) return false;
      if (b.stockLocationId !== transaction.product.toStockLocationId) return false;
      if (!inboundAllowedBinIds.has(b.id)) return false;
      if (!transaction.product.pmdId) return true;
      return validateBinConstraints(transaction.product.toStockLocationId, b.id, transaction.product.pmdId);
    });

    if (!productMasterData) return validBins;

    return validBins.sort(
      (a, b) =>
        (binsForProduct.get(b.id) || 0) - (binsForProduct.get(a.id) || 0) ||
        parseInt(a.name.match(/\d+/)?.[0] || '0', 10) - parseInt(b.name.match(/\d+/)?.[0] || '0', 10),
    );
  }, [transaction.product.toStockLocationId]);

  useEffect(() => {
    if (isDestinationBinDisabled()) return;

    if (validBins.length === 1 && !transaction.product.toBinId) {
      const container = containers.get(transaction.product.toContainerId || '');
      setTransaction(transaction =>
        transaction.withToBinId(productMasterData, validBins[0].id, container?.binId === SystemBin.id),
      );
    }
  }, [transaction.product.toStockLocationId]);

  const toElement = () => {
    if (!productMasterData) return undefined;
    return (item: Bin) => (
      <Grid container>
        <Grid item flexGrow={1}>
          <p>{item.name}</p>
        </Grid>
        <Grid item my={'auto'}>
          <p className='text-sm text-gray-400'>{`${UnitOfMeasure.toQuantityWithSuffix(
            productMasterData.unitOfMeasure,
            (binsForProduct.get(item.id) || 0).toString(),
          )}`}</p>
        </Grid>
      </Grid>
    );
  };

  if (transaction.type === ProductTransactionType.outbound) return null;

  return (
    <Grid item xs={6}>
      <DropdownSearchSelect
        mandatory
        disabled={isDestinationBinDisabled()}
        placeholder={t(TranslationKey.destinationBin).singular.label}
        label={t(TranslationKey.destinationBin).singular.label}
        items={validBins}
        value={bins.get(transaction.product.toBinId || '')}
        textBuilder={item => `${item.name}`}
        itemBuilder={toElement()}
        onSelect={bin => {
          const container = containers.get(transaction.product.toContainerId || '');
          setTransaction(transaction =>
            transaction.withToBinId(productMasterData, bin?.id, container?.binId === SystemBin.id || allowBinMismatch),
          );
        }}
      />
    </Grid>
  );
}
